import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { UHCActivationForm } from './UHCActivationForm';
import {
  ACTIVATE_UHC_SUBSCRIPTION,
  HAS_UHC_ORDER_HISTORY,
  LINK_UHC_EXISTING_USER,
  SEND_UHC_MAGIC_LINK,
  SIGNUP,
} from '../../services/ApolloClient';
import { useAuth, useApp } from '../../contexts';
import { getImpactFields, getUtmFields, navigateWithReload, removeFields } from '../../utils';
import { API } from '../../services/API/BaseAPI';
import { ErrorsService } from '../../services/ErrorsService';
import { registerFields } from '../../services/Constants/FormConstants';
import { config } from '../../services/Constants/AppConstants';
import { trackWebViewedEvent } from '../../services/DataPipeline/DataPipeline';
import { EDataKey, TData } from '../../types/forms';
import './UHCActivationAccount.scss';
import { InfoModal } from '../../components/InfoModal';
import { ConfirmationLinkedAccountModal, ConfirmationNewAccountModal } from '../../components/UHC';
import { ErrorModal } from '../../components/ErrorModal';
import Spinner from '../../components/Spinner/Spinner';

export const UHC_ORDER_POLL_INTERVAL_MS = 10_000;
export const UHC_ORDER_POLL_MAX_ATTEMPTS = 6;
// A check that never answers must not pin the modal on "Checking..." with no way out.
export const UHC_ORDER_CHECK_TIMEOUT_MS = 15_000;
const UHC_STORE_URL = 'https://member.uhc.com/';

// 'cancelled': a newer poll chain took over, or the page unmounted; the caller must do nothing.
type OrderCheckResult = 'found' | 'not-found' | 'failed' | 'cancelled';
type OrderCheckAnswer = { outcome: Exclude<OrderCheckResult, 'cancelled'>; error?: string };
type OrderCheckStatus = 'idle' | 'polling' | 'not-found' | 'failed';
type OrderCheckSource = 'form' | 'auto';

const ORDER_CHECK_COPY: Record<
  Exclude<OrderCheckStatus, 'idle'>,
  { title: string; button: string }
> = {
  polling: { title: 'Finishing your activation', button: 'Checking...' },
  'not-found': { title: "We still can't see your order", button: 'Check again' },
  failed: { title: "We couldn't check your order", button: 'Try again' },
};

const orderCheckInfo = (status: OrderCheckStatus, attempt: number): string => {
  if (status === 'polling') {
    return `Your order is still syncing with the UHC Store. We are checking again automatically (attempt ${attempt} of ${UHC_ORDER_POLL_MAX_ATTEMPTS}).`;
  }
  if (status === 'failed') {
    return "We couldn't check your order with the UHC Store right now. Please try again in a moment, or contact support if it keeps happening.";
  }
  return "We still can't see your order in the UHC Store. You can check again, go back to the UHC Store, or contact support.";
};

const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

const withTimeout = <T,>(promise: Promise<T>, ms: number, onTimeout: () => void): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      onTimeout();
      reject(new Error(`order check timed out after ${ms}ms`));
    }, ms);
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      error => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

function UHCActivationPage() {
  const [formError, setFormError] = useState({});
  const [signup] = useMutation(SIGNUP);
  const [sendUhcMagicLink] = useMutation(SEND_UHC_MAGIC_LINK);
  const [activateUHCSubscription] = useMutation(ACTIVATE_UHC_SUBSCRIPTION);
  const [linkUhcExistingUser] = useMutation(LINK_UHC_EXISTING_USER);

  const { login, signed, userInfo } = useAuth();
  const { setLoading } = useApp();
  const [searchParams] = useSearchParams();

  const [showSendMagicLinkModal, setShowSendMagicLinkModal] = useState(false);
  const [showMissingDataModal, setShowMissingDataModal] = useState(false);
  const [showPasswordStepMessage, setShowPasswordStepMessage] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showNewAccountModal, setShowNewAccountModal] = useState(false);
  const [orderCheck, setOrderCheck] = useState<{ status: OrderCheckStatus; attempt: number }>({
    status: 'idle',
    attempt: 0,
  });
  const [orderRetryCount, setOrderRetryCount] = useState(0);
  const [activationRetryCount, setActivationRetryCount] = useState(0);
  const orderCheckSource = useRef<OrderCheckSource>('form');
  // Each poll chain gets a generation number; starting a new chain (or unmounting) retires the older ones, so two
  // chains can never interleave their state updates.
  const orderPollGeneration = useRef(0);
  const [showUhcActivationErrorModal, setShowUhcActivationErrorModal] = useState(false);
  const [isAutoActivating, setIsAutoActivating] = useState(false);

  const [showLoggedInMismatchModal, setShowLoggedInMismatchModal] = useState(false);
  const [showLoggedInActivatedModal, setShowLoggedInActivatedModal] = useState(false);
  const isSubmitInFlight = useRef(false);

  const magicToken = searchParams.get('magic_token');

  const uhcPendingSamlId = localStorage.getItem('uhc_pending_saml_id');
  const uhcPendingLinkId = localStorage.getItem('uhc_pending_link_id');
  const hasPasswordSet = localStorage.getItem('uhc_pending_link_has_password') === 'true';

  const isUhcLinkFlow = searchParams.get('link') === 'true';
  const isSamlFlow = searchParams.get('saml') === 'true';

  const uhcPendingEmail = localStorage.getItem('uhc_pending_email');

  // no-cache: the same saml id is re-checked while the UHC order syncs, a cached "false" would defeat that.
  const [checkOrderHistory] = useLazyQuery(HAS_UHC_ORDER_HISTORY, { fetchPolicy: 'no-cache' });

  const getEmailFromMagicToken = (token: string | null): string | null => {
    if (!token) return null;
    try {
      const decoded = jwtDecode<{ email: string }>(token);
      return decoded.email;
    } catch (err) {
      ErrorsService.message(
        `[UHCActivation] Invalid magic token: ${err instanceof Error ? err.message : String(err)}`,
        { extra: { error: err } },
      );
      return null;
    }
  };

  const prefilledEmail = useMemo(() => {
    return getEmailFromMagicToken(magicToken) ?? uhcPendingEmail;
  }, [magicToken, uhcPendingEmail]);

  const fields = registerFields.map(field =>
    field.key === EDataKey.email ? { ...field, isDisabled: true } : field,
  );

  // Clear pending fields if we're in magic flow
  useEffect(() => {
    if (magicToken) {
      localStorage.removeItem('uhc_pending_email');
      localStorage.removeItem('uhc_pending_link_id');
      localStorage.removeItem('uhc_pending_saml_id');
      localStorage.removeItem('uhc_pending_link_has_password');
    }
  }, [magicToken]);

  // Validate flow + required storage fields
  useEffect(() => {
    const isMagicFlow = Boolean(magicToken);

    if (!isUhcLinkFlow && !isSamlFlow && !isMagicFlow) {
      navigateWithReload('/');
      return;
    }

    const missingLinkFlowFields =
      isUhcLinkFlow && (!uhcPendingEmail || !uhcPendingLinkId || hasPasswordSet == null);

    const missingSamlFlowFields = isSamlFlow && (!uhcPendingEmail || !uhcPendingSamlId);

    if ((isUhcLinkFlow && missingLinkFlowFields) || (isSamlFlow && missingSamlFlowFields)) {
      setShowMissingDataModal(true);
    }
  }, [
    isUhcLinkFlow,
    isSamlFlow,
    magicToken,
    uhcPendingEmail,
    uhcPendingLinkId,
    uhcPendingSamlId,
    hasPasswordSet,
  ]);

  const checkUhcOrderHistory = useCallback(
    async (samlId: string | null): Promise<OrderCheckAnswer> => {
      if (!samlId) return { outcome: 'not-found' };
      // Rejecting our wrapper is not enough: Apollo deduplicates identical in-flight queries, so a retry would
      // join the hung request. Abort the fetch itself and opt this operation out of deduplication.
      const controller = new AbortController();
      try {
        // Apollo resolves with { error } rather than rejecting; a failed request must not read as "no order".
        const result = await withTimeout(
          checkOrderHistory({
            variables: { samlId },
            context: { queryDeduplication: false, fetchOptions: { signal: controller.signal } },
          }),
          UHC_ORDER_CHECK_TIMEOUT_MS,
          () => controller.abort(),
        );
        if (result.error) return { outcome: 'failed', error: errorMessage(result.error) };
        return { outcome: result.data?.hasUhcOrderHistory === true ? 'found' : 'not-found' };
      } catch (error) {
        return { outcome: 'failed', error: errorMessage(error) };
      }
    },
    [checkOrderHistory],
  );

  // Poll while the UHC order syncs, keeping the member on the page. Resolves 'found', 'not-found' after the
  // last attempt, 'failed' as soon as a check itself errors, or 'cancelled' when a newer chain took over.
  const waitForUhcOrder = useCallback(
    async (samlId: string | null, generation: number, attempt = 1): Promise<OrderCheckResult> => {
      const superseded = () => generation !== orderPollGeneration.current;
      if (attempt > 1) await sleep(UHC_ORDER_POLL_INTERVAL_MS);
      if (superseded()) return 'cancelled';
      setOrderCheck({ status: 'polling', attempt });

      const { outcome, error } = await checkUhcOrderHistory(samlId);
      if (superseded()) return 'cancelled';
      if (outcome === 'found') {
        setOrderCheck({ status: 'idle', attempt: 0 });
        return 'found';
      }
      if (outcome === 'failed') {
        ErrorsService.message('[UHCActivation] order history check failed', {
          level: 'warning',
          extra: { attempt, source: orderCheckSource.current, error },
        });
        setOrderCheck({ status: 'failed', attempt });
        return 'failed';
      }
      if (attempt < UHC_ORDER_POLL_MAX_ATTEMPTS)
        return waitForUhcOrder(samlId, generation, attempt + 1);

      setOrderCheck({ status: 'not-found', attempt });
      return 'not-found';
    },
    [checkUhcOrderHistory],
  );

  // Retire any running chain and start a fresh one owned by `source`.
  const startOrderPoll = useCallback(
    (samlId: string | null, source: OrderCheckSource) => {
      orderPollGeneration.current += 1;
      const generation = orderPollGeneration.current;
      orderCheckSource.current = source;
      return {
        generation,
        result: waitForUhcOrder(samlId, generation),
        isCurrent: () => generation === orderPollGeneration.current,
      };
    },
    [waitForUhcOrder],
  );

  useEffect(
    () => () => {
      orderPollGeneration.current += 1;
    },
    [],
  );

  useEffect(() => {
    if (orderCheck.status === 'not-found') {
      trackWebViewedEvent({ modal_viewed: 'UhcMissingOrder' });
    }
  }, [orderCheck.status]);

  useEffect(() => {
    const shouldGuard = isUhcLinkFlow && hasPasswordSet === true && !magicToken;

    if (!shouldGuard || signed !== true) return undefined;

    const targetEmail = (prefilledEmail ?? '').trim().toLowerCase();
    const currentEmailRaw = userInfo?.email;
    const currentEmail = (currentEmailRaw ?? '').trim().toLowerCase();

    if (!targetEmail) return undefined;

    if (!currentEmailRaw) {
      setLoading(true);
      return undefined;
    }

    setLoading(false);

    // Wrong account logged in
    if (currentEmail !== targetEmail) {
      setShowLoggedInMismatchModal(true);
      return undefined;
    }

    const samlIdToUse = uhcPendingLinkId;

    if (!samlIdToUse) return undefined;
    // The form behind the modal is still reachable by keyboard; while this chain owns the page it must not
    // start a second one.
    if (isSubmitInFlight.current) return undefined;
    isSubmitInFlight.current = true;

    const poll = startOrderPoll(samlIdToUse, 'auto');

    (async () => {
      try {
        const orderResult = await poll.result;
        if (orderResult !== 'found') return;

        setIsAutoActivating(true);
        try {
          const shouldLinkAccount = localStorage.getItem('uhc_is_linked_account') === 'false';

          await activateUHCSubscription({
            variables: { samlId: samlIdToUse, linkAccount: shouldLinkAccount },
          });
          if (!poll.isCurrent()) return;

          // cleanup pending state
          localStorage.removeItem('uhc_pending_link_id');
          localStorage.removeItem('uhc_pending_email');
          localStorage.removeItem('uhc_pending_saml_id');
          localStorage.removeItem('uhc_pending_link_has_password');

          setShowLoggedInActivatedModal(true);
        } catch (e) {
          if (!poll.isCurrent()) return;
          // Stable title so Sentry groups these; the server message goes in extra.
          ErrorsService.message('[UHCActivation] activation failed', {
            extra: { error: e, message: errorMessage(e), source: 'auto' },
          });
          setShowUhcActivationErrorModal(true);
        } finally {
          // A retired chain settling its mutation must not switch off a spinner a newer chain is showing.
          if (poll.isCurrent()) setIsAutoActivating(false);
        }
      } finally {
        // A retired chain must not release a flag a newer chain now holds; the cleanup released ours already.
        if (poll.isCurrent()) isSubmitInFlight.current = false;
      }
    })();

    // A dependency change (retry counters included) retires this chain before the next run starts its own.
    // The retired chain may still be sleeping or awaiting its mutation, so release everything it owns here: the
    // in-flight flag, the modal and the spinner. Otherwise the next run returns early on the flag and the member
    // is stranded on "Checking..." or on "Activating...".
    return () => {
      orderPollGeneration.current += 1;
      isSubmitInFlight.current = false;
      setIsAutoActivating(false);
      setOrderCheck(current =>
        current.status === 'polling' ? { status: 'idle', attempt: 0 } : current,
      );
    };
  }, [
    signed,
    userInfo?.email,
    prefilledEmail,
    isUhcLinkFlow,
    hasPasswordSet,
    magicToken,
    uhcPendingLinkId,
    activateUHCSubscription,
    setIsAutoActivating,
    startOrderPoll,
    setLoading,
    orderRetryCount,
    activationRetryCount,
  ]);

  const submitActivation = async (formData: Partial<TData>) => {
    // Link existing user social login flow. The server links the UHC identity and activates the
    // subscription from the magic token alone, so this works even when the email is opened on a
    // device that never had the pending link id in localStorage.
    if (magicToken) {
      try {
        setLoading(true);
        setFormError({});

        const res = await linkUhcExistingUser({
          variables: {
            token: magicToken,
            newPassword: formData.password,
            newPasswordConfirm: formData.confirmPassword,
          },
        });

        login(res.data?.linkUhcExistingUser?.token);
        await API.post('auth-cookie');

        removeFields();
        localStorage.removeItem('uhc_pending_link_id');

        setShowConfirmationModal(true);
        setLoading(false);
        return;
      } catch (e) {
        const err = e as Error;
        setLoading(false);
        setFormError({
          message: err.message,
          fields: [EDataKey.email],
        });
        return;
      }
    }

    try {
      setLoading(true);
      setFormError({});

      const analyticsFields = getUtmFields();
      const impactFields = getImpactFields();

      const variables: any = {
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        analytics: analyticsFields,
        impact: impactFields,
      };

      if (uhcPendingSamlId) {
        variables.uhcDetails = { samlId: uhcPendingSamlId, email: formData.email };
      }

      const res = await signup({ variables });

      // Activate UHC subscription
      await activateUHCSubscription({
        variables: { samlId: uhcPendingSamlId },
      });

      removeFields();
      login(res.data?.signUp?.token);
      await API.post('auth-cookie');

      setShowNewAccountModal(true);
      setLoading(false);
    } catch (e) {
      const err = e as Error;
      setLoading(false);
      setFormError({
        message: err.message,
        fields: [EDataKey.email],
      });
    }
  };

  const handleActivation = async (formData: Partial<TData>) => {
    // Same re-entry guard as the email step: a second Enter during the request would link or sign up twice.
    if (isSubmitInFlight.current) return;
    isSubmitInFlight.current = true;
    try {
      await submitActivation(formData);
    } finally {
      isSubmitInFlight.current = false;
    }
  };

  const handleSendMagicLink = async (): Promise<boolean> => {
    try {
      setFormError({});
      await sendUhcMagicLink({ variables: { pendingLinkId: uhcPendingLinkId } });
      return true;
    } catch (e) {
      const err = e as Error;
      setFormError({
        message: err.message,
        fields: [EDataKey.email],
      });
      return false;
    }
  };

  const handleEmailStepContinue = async (): Promise<boolean> => {
    // The global loader blocks clicks but not the keyboard, so a second Enter while the order check or the
    // magic-link send is running would trigger another email. Guard against re-entry.
    if (isSubmitInFlight.current) return false;
    isSubmitInFlight.current = true;

    try {
      // The order check shows its own progress modal, so the global loader only starts afterwards.
      if (uhcPendingLinkId || uhcPendingSamlId) {
        const orderResult = await startOrderPoll(uhcPendingSamlId || uhcPendingLinkId, 'form')
          .result;
        if (orderResult !== 'found') return false;
      }
      setLoading(true);

      if (isUhcLinkFlow && !hasPasswordSet) {
        // Only tell the member to check their inbox once the server confirmed the send.
        const sent = await handleSendMagicLink();
        if (sent) setShowSendMagicLinkModal(true);
        return false;
      }

      if (isUhcLinkFlow) {
        // Existing account with a password and no session: sign in, then activate. Signing up here would
        // fail, signUp only accepts a new-member saml id. The login page reads the same pending keys.
        navigateWithReload('/uhc/login/');
        return false;
      }

      setShowPasswordStepMessage(true);
      return true;
    } finally {
      isSubmitInFlight.current = false;
      setLoading(false);
    }
  };

  const retryOrderCheck = () => {
    if (orderCheck.status === 'polling') return;
    if (orderCheckSource.current === 'auto') {
      // The effect re-run starts a chain that sets "polling" itself; resetting to idle first would unmount the
      // modal for a frame and drop keyboard focus.
      setOrderRetryCount(count => count + 1);
    } else {
      setOrderCheck({ status: 'idle', attempt: 0 });
      handleEmailStepContinue();
    }
  };

  const retryActivation = () => {
    setShowUhcActivationErrorModal(false);
    setActivationRetryCount(count => count + 1);
  };

  if (isAutoActivating) {
    return (
      <div className="uhc-activation-loading-page">
        <Helmet>
          <title>Activating… | Every Mother</title>
        </Helmet>

        <div className="uhc-activation-loading-card">
          <h1 className="uhc-activation-title">Activating your subscription…</h1>
          <p className="uhc-activation-text">
            This may take a moment. Please don’t close this page.
          </p>

          <div className="uhc-activation-spinner">
            <Spinner />
          </div>
        </div>
      </div>
    );
  }

  if (showConfirmationModal) {
    return (
      <ConfirmationLinkedAccountModal
        isOpen={showConfirmationModal}
        onOkClick={() => navigateWithReload('/app/your-path/')}
      />
    );
  }

  if (showNewAccountModal) {
    return (
      <ConfirmationNewAccountModal
        isOpen={showNewAccountModal}
        onOkClick={() => navigateWithReload('/app/your-path/')}
      />
    );
  }

  if (showLoggedInActivatedModal) {
    return (
      <ConfirmationLinkedAccountModal
        isOpen={showLoggedInActivatedModal}
        onOkClick={() => navigateWithReload('/app/your-path/')}
      />
    );
  }

  if (showMissingDataModal) {
    return (
      <InfoModal
        isOpen={showMissingDataModal}
        title="Activation Error"
        info1="We couldn’t validate your activation session. Please try starting again from the UHC Store."
        onOkClick={() => {
          setShowMissingDataModal(false);
          navigateWithReload('/');
        }}
        buttonLabel="Go Home"
      />
    );
  }

  if (showLoggedInMismatchModal) {
    return (
      <InfoModal
        isOpen={showLoggedInMismatchModal}
        title="Sign in to finish activation"
        info1={`This UHC activation is for ${prefilledEmail}. Please sign in to that account to continue.`}
        onOkClick={() => {
          setShowLoggedInMismatchModal(false);
          navigateWithReload('/uhc/login/');
        }}
        buttonLabel="Sign In"
      />
    );
  }

  return (
    <div className="register-login-container">
      <Helmet>
        <title>Activate Subscription | Every Mother</title>
      </Helmet>

      <div className="register-login-content">
        <h1 className="title-activation">Activate Your Subscription</h1>

        {showPasswordStepMessage || Boolean(magicToken) ? (
          <p>Please create a password to complete your activation.</p>
        ) : (
          <p>Almost there! Click the button below to begin your activation</p>
        )}

        <UHCActivationForm
          fields={fields}
          handleForm={handleActivation}
          formError={formError}
          isMagicLink={Boolean(magicToken)}
          onEmailStepContinue={handleEmailStepContinue}
          prefilledEmail={prefilledEmail}
          showAllFields={showPasswordStepMessage}
        />
      </div>

      <InfoModal
        isOpen={showSendMagicLinkModal}
        title="Check Your Email to Continue"
        info1="Your account was created using social login. We’ve sent a secure email with a link to set your password. Please check your inbox and follow the link to finish activating your subscription."
        onOkClick={() => {
          setShowSendMagicLinkModal(false);
          navigateWithReload('/');
        }}
        buttonLabel="OK"
      />

      {orderCheck.status !== 'idle' && (
        <InfoModal
          isOpen
          title={ORDER_CHECK_COPY[orderCheck.status].title}
          info1={
            // The text changes in place for up to a minute; announce it to screen readers.
            <span role="status" aria-live="polite">
              {orderCheckInfo(orderCheck.status, orderCheck.attempt)}
            </span>
          }
          onOkClick={retryOrderCheck}
          onClose={
            // Not while checking, and never for the signed-in activation: dismissing would leave that member
            // on a form whose path ends at the login page. Check again is the recovery there.
            orderCheck.status === 'polling' || orderCheckSource.current === 'auto'
              ? undefined
              : () => setOrderCheck({ status: 'idle', attempt: 0 })
          }
          isBusy={orderCheck.status === 'polling'}
          buttonLabel={ORDER_CHECK_COPY[orderCheck.status].button}
          footer={
            <p className="uhc-order-check-links">
              <a href={UHC_STORE_URL} rel="noopener noreferrer">
                Return to UHC Store
              </a>
              {' · '}
              <a href={`${config.WEBSITE_URL}/#contact`} rel="noopener noreferrer">
                Contact support
              </a>
            </p>
          }
        />
      )}

      <ErrorModal
        isOpen={showUhcActivationErrorModal}
        title="UHC Activation Incomplete"
        message="We’re having trouble finishing your UHC activation. You can try again, or return to the UHC Store and click Activate Subscription. If the issue persists, please contact our support team."
        buttonText="Try again"
        onSubmit={retryActivation}
        secondaryButtonText="Return to UHC Store"
        onSecondarySubmit={() => {
          window.location.href = UHC_STORE_URL;
        }}
      />
    </div>
  );
}

export default UHCActivationPage;
